import { clamp01 } from "../math.js";
import { PALETTE_SIZE } from "../data/types.js";
import {
  createShaderProgram,
  createUnitQuad,
  paletteUniformData,
  requireAttribute,
  requireBuffer,
  UniformLocations,
} from "./shader-program.js";
import type { HalftoneData } from "../data/types.js";
import type { BeatKind } from "../core/wing.js";
import type { ClipTransform } from "./layout.js";

// Closed, both wing planes lie flat in the portrait's own plane.
export const WING_NEAR_REST_ANGLE = 0;
export const WING_FAR_REST_ANGLE = 0;

export const WING_INSTANCE_FIELDS = 6;
export const WING_ORIGIN_X = 0.2124;
export const WING_ORIGIN_Y = 0.5056;

export interface WingPlaneConfig {
  pivotX: number;
  pivotY: number;
  cameraDistance: number;
  focalLength: number;
}

export const WING_FAR_BASE_Y_ANGLE = (10 * Math.PI) / 180;

export const WING_PLANE_DEFAULTS: Readonly<WingPlaneConfig> = {
  pivotX: WING_ORIGIN_X * 1024,
  pivotY: WING_ORIGIN_Y * 1024,
  cameraDistance: 1000,
  focalLength: 1000,
};

export interface WingSourcePoint {
  x: number;
  y: number;
  radius: number;
}

export interface WingPlanePoint {
  span: number;
  chord: number;
  radius: number;
}

export interface WingWorldPoint {
  x: number;
  y: number;
  z: number;
}

export interface WingPlaneRotation {
  xRad: number;
  yRad: number;
  zRad: number;
}

export interface WingProjectedPoint extends WingWorldPoint {
  radius: number;
  cameraDepth: number;
}

export interface WingPlaneInstances {
  values: Float32Array;
  count: number;
}

export interface WingPlaneScatter {
  near: number;
  far: number;
}

// A plane is fully scattered once it's turned half a turn from rest. A full flap turns the near
// plane about three quarters of that, so the dots loosen up without falling apart.
const WING_SCATTER_TURN = Math.PI;

// Each plane scatters by its own turn, so the scatter always looks tied to that plane's motion.
export function wingPlaneScatter(
  kind: BeatKind,
  nearTurnRad: number,
  farTurnRad: number,
): WingPlaneScatter {
  if (kind === "flutter") return { near: 0, far: 0 };
  return {
    near: clamp01(nearTurnRad / WING_SCATTER_TURN),
    far: clamp01(farTurnRad / WING_SCATTER_TURN),
  };
}

/** Finds the point on the rigid plane that lands exactly on one baked dot when the wing is at
    rest. */
function calibrateWingPoint(
  source: WingSourcePoint,
  restAngleRad: number,
  config: WingPlaneConfig = WING_PLANE_DEFAULTS,
): WingPlanePoint {
  const dx = source.x - config.pivotX;
  const dy = source.y - config.pivotY;
  const sinAngle = Math.sin(restAngleRad);
  const cosAngle = Math.cos(restAngleRad);
  const denominator = dx * sinAngle - config.focalLength * cosAngle;
  if (Math.abs(denominator) < 1e-6) throw new Error("WingPlaneLayer: rest calibration is singular");
  const span = (dx * config.cameraDistance) / denominator;
  const cameraDepth = config.cameraDistance - span * sinAngle;
  if (cameraDepth <= 0) throw new Error("WingPlaneLayer: rest point is behind the camera");
  const chord = (dy * cameraDepth) / config.focalLength;

  return {
    span,
    chord,
    radius: (source.radius * cameraDepth) / config.focalLength,
  };
}

/** The plane's rotation, Rx·Ry·Rz, as a column-major 3x3 matrix, written into `out`. A point
    on the plane is (-span, 0, chord). The shader just multiplies by this matrix, so the CPU's
    geometry and the GPU's wings always agree on the rotation. */
export function wingPlaneMatrixInto(
  out: Float32Array | number[],
  rotationOrZ: number | WingPlaneRotation,
): Float32Array | number[] {
  const rotation =
    typeof rotationOrZ === "number" ? { xRad: 0, yRad: 0, zRad: rotationOrZ } : rotationOrZ;
  const cosX = Math.cos(rotation.xRad);
  const sinX = Math.sin(rotation.xRad);
  const cosY = Math.cos(rotation.yRad);
  const sinY = Math.sin(rotation.yRad);
  const cosZ = Math.cos(rotation.zRad);
  const sinZ = Math.sin(rotation.zRad);
  out[0] = cosY * cosZ;
  out[1] = -sinZ * cosX + sinY * cosZ * sinX;
  out[2] = -sinZ * sinX - sinY * cosZ * cosX;
  out[3] = cosY * sinZ;
  out[4] = cosZ * cosX + sinY * sinZ * sinX;
  out[5] = cosZ * sinX - sinY * sinZ * cosX;
  out[6] = sinY;
  out[7] = -cosY * sinX;
  out[8] = cosY * cosX;
  return out;
}

/** Rotates one point on a rigid plane. */
export function wingWorldPoint(
  point: WingPlanePoint,
  rotationOrZ: number | WingPlaneRotation,
): WingWorldPoint {
  const m = wingPlaneMatrixInto(
    Array.from({ length: 9 }, () => 0),
    rotationOrZ,
  );
  const x = -point.span;
  const z = point.chord;
  return {
    x: (m[0] ?? 0) * x + (m[6] ?? 0) * z,
    y: (m[1] ?? 0) * x + (m[7] ?? 0) * z,
    z: (m[2] ?? 0) * x + (m[8] ?? 0) * z,
  };
}

/** Projects a rotated plane point through the fixed camera. */
export function projectWingPoint(
  point: WingPlanePoint,
  rotationOrZ: number | WingPlaneRotation,
  config: WingPlaneConfig = WING_PLANE_DEFAULTS,
): WingProjectedPoint {
  const world = wingWorldPoint(point, rotationOrZ);
  const cameraDepth = config.cameraDistance - world.y;
  if (cameraDepth <= 0) throw new Error("WingPlaneLayer: point is behind the camera");
  return {
    x: config.pivotX + (config.focalLength * world.x) / cameraDepth,
    y: config.pivotY + (config.focalLength * world.z) / cameraDepth,
    z: world.z,
    radius: (point.radius * config.focalLength) / cameraDepth,
    cameraDepth,
  };
}

/** Puts every baked dot on both rigid planes, so at rest the whole silhouette is still there. */
export function buildWingPlaneInstances(data: HalftoneData): WingPlaneInstances {
  const values = new Float32Array(data.n * 2 * WING_INSTANCE_FIELDS);
  for (let plane = 0; plane < 2; plane++) {
    const restAngle = plane === 0 ? WING_FAR_REST_ANGLE : WING_NEAR_REST_ANGLE;
    for (let i = 0; i < data.n; i++) {
      const local = calibrateWingPoint(
        {
          x: data.x[i] ?? 0,
          y: data.y[i] ?? 0,
          radius: data.r[i] ?? 0,
        },
        restAngle,
      );
      const offset = (plane * data.n + i) * WING_INSTANCE_FIELDS;
      values[offset] = local.span;
      values[offset + 1] = local.chord;
      values[offset + 2] = local.radius;
      values[offset + 3] = data.colorIndex[i] ?? 0;
      values[offset + 4] = plane;
      values[offset + 5] = data.opacity?.[i] ?? 1;
    }
  }
  return { values, count: data.n * 2 };
}

function glslNumber(value: number): string {
  return value.toPrecision(12);
}

const VERTEX_SHADER_SRC = `#version 300 es
in vec2 aCorner;
in float aSpan;
in float aChord;
in float aRadius;
in float aColorIdx;
in float aPlane;
in float aOpacity;
uniform vec4 uHome;
uniform float uMinRadius;
uniform mat3 uNearMatrix;  // wingPlaneMatrixInto, column-major
uniform mat3 uFarMatrix;
uniform float uFarOpacity;
uniform float uFarShade;
uniform float uNow;
uniform float uNearScatter;
uniform float uFarScatter;
uniform vec3 uPalette[${PALETTE_SIZE}];
out vec2 vCorner;
out vec3 vColor;
out float vOpacity;

const vec2 PIVOT = vec2(${glslNumber(WING_PLANE_DEFAULTS.pivotX)}, ${glslNumber(WING_PLANE_DEFAULTS.pivotY)});
const float CAMERA_DISTANCE = ${glslNumber(WING_PLANE_DEFAULTS.cameraDistance)};
const float FOCAL_LENGTH = ${glslNumber(WING_PLANE_DEFAULTS.focalLength)};

float hash(float n){ return fract(sin(n) * 43758.5453); }

void main(){
  vCorner = aCorner;
  vec3 world = (aPlane > 0.5 ? uNearMatrix : uFarMatrix) * vec3(-aSpan, 0.0, aChord);
  float cameraDepth = CAMERA_DISTANCE - world.y;
  vec2 artwork = PIVOT + vec2(FOCAL_LENGTH * world.x, FOCAL_LENGTH * world.z) / cameraDepth;
  vec2 center = artwork * uHome.xy + uHome.zw;
  float radius = max(uMinRadius, aRadius * FOCAL_LENGTH * abs(uHome.x) / cameraDepth);
  float opacity = clamp(mix(uFarOpacity, 1.0, aPlane), 0.0, 1.0) * aOpacity;
  {
    float id = float(gl_InstanceID);
    float h1 = hash(id * 1.7);
    float h2 = hash(id * 3.3 + 11.0);
    float h3 = hash(id * 5.1 + 23.0);
    float edge = smoothstep(25.0, 85.0, length(vec2(aSpan, aChord)));
    float incoh = clamp(mix(uFarScatter, uNearScatter, aPlane) * edge * 0.8, 0.0, 1.0);
    vec2 jitter = vec2(
      sin(uNow * 6.1 + h1 * 40.0) + 0.5 * sin(uNow * 11.3 + h3 * 71.0),
      cos(uNow * 5.3 + h2 * 40.0) + 0.5 * cos(uNow * 9.7 + h1 * 61.0)
    ) * 0.024 * incoh;
    center += jitter;
    radius *= 1.0 + 0.55 * sin(uNow * (7.0 + h1 * 5.0) + h3 * 6.2831853) * incoh;
    if (hash(id * 7.3 + 5.0) < 0.5) {
      float flicker = 0.5 + 0.5 * sin(uNow * (5.0 + h3 * 6.0) + h2 * 6.2831853);
      opacity *= mix(1.0, mix(0.10, 1.0, flicker), incoh);
    }
  }
  float depthShade = mix(uFarShade, 1.0, aPlane);
  vColor = uPalette[int(aColorIdx + 0.5)] * depthShade;
  vOpacity = opacity;
  float depth = clamp((cameraDepth / CAMERA_DISTANCE - 1.0) * 2.0, -0.95, 0.95);
  gl_Position = vec4(center + aCorner * radius, depth, 1.0);
}
`;

const FRAGMENT_SHADER_SRC = `#version 300 es
precision highp float;
in vec2 vCorner;
in vec3 vColor;
in float vOpacity;
out vec4 fragColor;
void main(){
  if (dot(vCorner, vCorner) > 1.0) discard;
  fragColor = vec4(vColor * vOpacity, vOpacity);
}
`;

export interface WingPlaneFrame {
  home: ClipTransform;
  now: number;
  nearRotation: WingPlaneRotation;
  farRotation: WingPlaneRotation;
  nearScatter: number;
  farScatter: number;
  farOpacity: number;
  /** How bright the far plane's colors are, as a fraction of the palette's: it's in the near
      plane's shadow. */
  farShade: number;
}

const WING_PLANE_UNIFORMS = [
  "uHome",
  "uMinRadius",
  "uNearMatrix",
  "uFarMatrix",
  "uFarOpacity",
  "uFarShade",
  "uNow",
  "uNearScatter",
  "uFarScatter",
  "uPalette",
] as const;

export class WingPlaneLayer {
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly quadBuffer: WebGLBuffer;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly instanceCount: number;
  // Reused every frame, so drawing allocates nothing here.
  private readonly nearMatrix = new Float32Array(9);
  private readonly farMatrix = new Float32Array(9);
  private readonly uniforms: UniformLocations<(typeof WING_PLANE_UNIFORMS)[number]>;

  static async create(
    gl: WebGL2RenderingContext,
    data: HalftoneData,
    signal?: AbortSignal,
  ): Promise<WingPlaneLayer> {
    const program = await createShaderProgram(gl, VERTEX_SHADER_SRC, FRAGMENT_SHADER_SRC, signal);
    try {
      signal?.throwIfAborted();
      return new WingPlaneLayer(gl, data, program);
    } catch (error) {
      gl.deleteProgram(program);
      throw error;
    }
  }

  private constructor(
    private readonly gl: WebGL2RenderingContext,
    data: HalftoneData,
    program: WebGLProgram,
  ) {
    const cleanup: Array<() => void> = [];
    try {
      const uniforms = new UniformLocations(gl, program, WING_PLANE_UNIFORMS, "WingPlaneLayer");

      const aCorner = requireAttribute(gl, program, "aCorner", "WingPlaneLayer");
      const aSpan = requireAttribute(gl, program, "aSpan", "WingPlaneLayer");
      const aChord = requireAttribute(gl, program, "aChord", "WingPlaneLayer");
      const aRadius = requireAttribute(gl, program, "aRadius", "WingPlaneLayer");
      const aColorIdx = requireAttribute(gl, program, "aColorIdx", "WingPlaneLayer");
      const aPlane = requireAttribute(gl, program, "aPlane", "WingPlaneLayer");
      const aOpacity = requireAttribute(gl, program, "aOpacity", "WingPlaneLayer");
      const vao = gl.createVertexArray();
      if (!vao) throw new Error("WingPlaneLayer: gl.createVertexArray returned null");
      cleanup.push(() => gl.deleteVertexArray(vao));
      gl.bindVertexArray(vao);

      const quadBuffer = createUnitQuad(gl, aCorner, "WingPlaneLayer");
      cleanup.push(() => gl.deleteBuffer(quadBuffer));

      const instances = buildWingPlaneInstances(data);

      const instanceBuffer = requireBuffer(gl, "WingPlaneLayer instance");
      cleanup.push(() => gl.deleteBuffer(instanceBuffer));
      gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, instances.values, gl.STATIC_DRAW);
      const fieldBytes = Float32Array.BYTES_PER_ELEMENT;
      const stride = WING_INSTANCE_FIELDS * fieldBytes;
      const attributes: ReadonlyArray<readonly [number, number]> = [
        [aSpan, 0],
        [aChord, 1],
        [aRadius, 2],
        [aColorIdx, 3],
        [aPlane, 4],
        [aOpacity, 5],
      ];
      for (const [location, offset] of attributes) {
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, 1, gl.FLOAT, false, stride, offset * fieldBytes);
        gl.vertexAttribDivisor(location, 1);
      }

      gl.bindVertexArray(null);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);
      this.program = program;
      this.vao = vao;
      this.quadBuffer = quadBuffer;
      this.instanceBuffer = instanceBuffer;
      this.instanceCount = instances.count;
      this.uniforms = uniforms;
    } catch (error) {
      for (const free of cleanup.reverse()) free();
      throw error;
    }
  }

  setPalette(colors: readonly string[]): void {
    this.gl.useProgram(this.program);
    this.gl.uniform3fv(this.uniforms.get("uPalette"), paletteUniformData(colors, PALETTE_SIZE));
  }

  resize(deviceWidth: number, deviceHeight: number): void {
    this.gl.viewport(0, 0, deviceWidth, deviceHeight);
  }

  render(frame: WingPlaneFrame): void {
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.uniform4f(
      this.uniforms.get("uHome"),
      frame.home.sx,
      frame.home.sy,
      frame.home.ox,
      frame.home.oy,
    );
    gl.uniform1f(this.uniforms.get("uMinRadius"), frame.home.minRadius);
    gl.uniformMatrix3fv(
      this.uniforms.get("uNearMatrix"),
      false,
      wingPlaneMatrixInto(this.nearMatrix, frame.nearRotation),
    );
    gl.uniformMatrix3fv(
      this.uniforms.get("uFarMatrix"),
      false,
      wingPlaneMatrixInto(this.farMatrix, frame.farRotation),
    );
    gl.uniform1f(this.uniforms.get("uFarOpacity"), frame.farOpacity);
    gl.uniform1f(this.uniforms.get("uFarShade"), frame.farShade);
    gl.uniform1f(this.uniforms.get("uNow"), frame.now);
    gl.uniform1f(this.uniforms.get("uNearScatter"), frame.nearScatter);
    gl.uniform1f(this.uniforms.get("uFarScatter"), frame.farScatter);
    gl.disable(gl.BLEND);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.depthFunc(gl.LEQUAL);
    gl.clearColor(0, 0, 0, 0);
    gl.clearDepth(1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    this.gl.deleteProgram(this.program);
    this.gl.deleteBuffer(this.quadBuffer);
    this.gl.deleteBuffer(this.instanceBuffer);
    this.gl.deleteVertexArray(this.vao);
  }
}
